import { useState, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { getRoute } from '../services/api';

/**
 * Generate a curved line (quadratic bezier) between two [lng, lat] points.
 * The curve always bows upward (toward higher latitude / top of screen).
 * @param {Array} start - [lng, lat]
 * @param {Array} end - [lng, lat]
 * @param {number} segments - number of interpolation points (more = smoother)
 * @param {number} curvature - how much the curve bows (0.2 = subtle, 0.5 = pronounced)
 * @returns {Array} array of [lng, lat] coordinates forming the curve
 */
function generateCurvedLine(start, end, segments = 50, curvature = 0.3) {
  const midX = (start[0] + end[0]) / 2;
  const midY = (start[1] + end[1]) / 2;

  // Perpendicular offset for the control point
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  // eslint-disable-next-line no-unused-vars
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Two possible perpendicular directions: (-dy, dx) and (dy, -dx)
  // Pick the one that pushes the control point upward (higher lat)
  const cp1Y = midY + dx * curvature;   // from (-dy, dx) perpendicular
  const cp2Y = midY - dx * curvature;   // from (dy, -dx) perpendicular

  let controlX, controlY;
  if (cp1Y >= cp2Y) {
    // (-dy, dx) direction gives higher latitude
    controlX = midX - dy * curvature;
    controlY = cp1Y;
  } else {
    // (dy, -dx) direction gives higher latitude
    controlX = midX + dy * curvature;
    controlY = cp2Y;
  }

  const coords = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const invT = 1 - t;
    // Quadratic bezier: B(t) = (1-t)²·P0 + 2(1-t)t·P1 + t²·P2
    const lng = invT * invT * start[0] + 2 * invT * t * controlX + t * t * end[0];
    const lat = invT * invT * start[1] + 2 * invT * t * controlY + t * t * end[1];
    coords.push([lng, lat]);
  }
  return coords;
}

/**
 * Custom hook for managing routing functionality
 */
export const useRouting = (mapRef, fetchLocationDetails, currentLocationMarkerRef = null, routePointDragHandlerRef = null) => {
  const [isRoutingMode, setIsRoutingMode] = useState(false);
  const [routePointA, setRoutePointA] = useState(null);
  const [routePointB, setRoutePointB] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [routeDetailsLoading, setRouteDetailsLoading] = useState(false);
  const [travelMode, setTravelMode] = useState('truck'); // Travel mode state - default to truck

  const routeMarkersRef = useRef([]);
  const routeArrowMarkersRef = useRef([]);
  const routeArrowZoomHandlerRef = useRef(null);
  const routeGeometryRef = useRef(null);
  const hasZoomedToRouteRef = useRef(false);
  const fetchRouteIdRef = useRef(0);
  const alternateRouteClickHandlerRef = useRef(null); // Ref for alternate route click callback

  // Clear route line, connectors, and duration popup without touching markers
  const clearRouteVisualization = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    if (routeArrowMarkersRef.current.length > 0) {
      routeArrowMarkersRef.current.forEach(marker => marker.remove());
      routeArrowMarkersRef.current = [];
    }
    if (routeArrowZoomHandlerRef.current && map.off) {
      map.off('zoomend', routeArrowZoomHandlerRef.current);
      routeArrowZoomHandlerRef.current = null;
    }
    routeGeometryRef.current = null;

    // Remove duration popup if exists
    if (map._routeDurationPopup) {
      map._routeDurationPopup.remove();
      map._routeDurationPopup = null;
    }

    // Remove nudge proximity handler if exists
    if (map._routeNudgeProximityHandler) {
      map.off('move', map._routeNudgeProximityHandler);
      map.off('zoom', map._routeNudgeProximityHandler);
      map._routeNudgeProximityHandler = null;
    }

    // Remove alternate route duration popups if exist
    if (map._alternateRouteDurationPopups) {
      map._alternateRouteDurationPopups.forEach(popup => popup.remove());
      map._alternateRouteDurationPopups = [];
    }

    // Remove alternate route nudge proximity handler
    if (map._altRouteNudgeProximityHandler) {
      map.off('move', map._altRouteNudgeProximityHandler);
      map.off('zoom', map._altRouteNudgeProximityHandler);
      map._altRouteNudgeProximityHandler = null;
    }

    try {
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-line')) {
        map.removeLayer('route-line');
      }
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-line-outline')) {
        map.removeLayer('route-line-outline');
      }
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-arrows')) {
        map.removeLayer('route-arrows');
      }
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-arrows-center')) {
        map.removeLayer('route-arrows-center');
      }
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-arrows-text')) {
        map.removeLayer('route-arrows-text');
      }
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-connector-start')) {
        map.removeLayer('route-connector-start');
      }
      if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer('route-connector-end')) {
        map.removeLayer('route-connector-end');
      }
      if (map.getStyle && map.getStyle() && map.getSource && map.getSource('route')) {
        map.removeSource('route');
      }
      if (map.getStyle && map.getStyle() && map.getSource && map.getSource('route-connector-start')) {
        map.removeSource('route-connector-start');
      }
      if (map.getStyle && map.getStyle() && map.getSource && map.getSource('route-connector-end')) {
        map.removeSource('route-connector-end');
      }
      // Remove stop connector layers and sources
      for (let i = 0; i < 20; i++) {
        if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer(`route-connector-stop-${i}`)) {
          map.removeLayer(`route-connector-stop-${i}`);
        }
        if (map.getStyle && map.getStyle() && map.getSource && map.getSource(`route-connector-stop-${i}`)) {
          map.removeSource(`route-connector-stop-${i}`);
        }
      }
      // Remove alternate route layers and sources
      for (let i = 0; i < 5; i++) {
        if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer(`alternate-route-line-${i}`)) {
          map.removeLayer(`alternate-route-line-${i}`);
        }
        if (map.getStyle && map.getStyle() && map.getLayer && map.getLayer(`alternate-route-outline-${i}`)) {
          map.removeLayer(`alternate-route-outline-${i}`);
        }
        if (map.getStyle && map.getStyle() && map.getSource && map.getSource(`alternate-route-${i}`)) {
          map.removeSource(`alternate-route-${i}`);
        }
      }
    } catch (error) {
      // Error clearing route layer/source
    }
  }, [mapRef]);

  // Calculate bearing between two points (for rider icon rotation)
  const calculateBearing = useCallback((lat1, lng1, lat2, lng2) => {
    const toRadians = (deg) => deg * (Math.PI / 180);
    const toDegrees = (rad) => rad * (180 / Math.PI);

    const dLng = toRadians(lng2 - lng1);
    const lat1Rad = toRadians(lat1);
    const lat2Rad = toRadians(lat2);

    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

    const bearing = toDegrees(Math.atan2(y, x));
    return (bearing + 360) % 360; // Normalize to 0-360
  }, []);

  // Add marker for route points (A or B)
  const addRouteMarker = useCallback((lat, lng, label, rooftopName = null, bearing = null, { isUserLocation = false } = {}) => {
    if (!mapRef.current) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    // Remove any existing marker with the same label
    const oldMarkerIndex = routeMarkersRef.current.findIndex(m => m._label === label);
    if (oldMarkerIndex !== -1) {
      routeMarkersRef.current[oldMarkerIndex].remove();
      routeMarkersRef.current.splice(oldMarkerIndex, 1);
    }

    const el = document.createElement('div');
    el.className = 'route-marker-container';

    // Create marker pin
    if (label.startsWith('stop-')) {
      // Use source icon for intermediate stops
      el.style.display = 'flex';
      el.style.flexDirection = 'row';
      el.style.alignItems = 'center';

      const markerPin = document.createElement('img');
      markerPin.src = '/icons/sourceIcon.png';
      markerPin.className = 'route-marker-pin-image';
      markerPin.style.width = '17.143px';
      markerPin.style.height = '17.143px';
      markerPin.style.cursor = 'default';
      markerPin.style.pointerEvents = 'none';
      markerPin.style.flexShrink = '0';
      el.appendChild(markerPin);

      // Add rooftop name label to the right of the pin for stops
      if (rooftopName && rooftopName !== 'Your Location') {
        const labelEl = document.createElement('div');
        labelEl.className = 'route-marker-label-stop';
        labelEl.textContent = rooftopName;
        labelEl.style.marginLeft = '4px';
        labelEl.style.color = '#2B2B2B';
        labelEl.style.fontFamily = 'Roboto, sans-serif';
        labelEl.style.fontSize = '14px';
        labelEl.style.fontWeight = '600';
        labelEl.style.fontStyle = 'normal';
        labelEl.style.lineHeight = 'normal';
        labelEl.style.whiteSpace = 'nowrap';
        labelEl.style.WebkitTextStroke = '1px #FFF';
        labelEl.style.paintOrder = 'stroke fill';
        labelEl.style.pointerEvents = 'none';
        el.appendChild(labelEl);
      }
    } else if (label === 'B') {
      // Use destination pin icon for Point B
      el.style.display = 'flex';
      el.style.flexDirection = 'row';
      el.style.alignItems = 'center';
      
      const markerPin = document.createElement('img');
      markerPin.src = '/icons/destinationPin.png';
      markerPin.className = 'route-marker-pin-image';
      markerPin.style.width = '32px';
      markerPin.style.height = '32px';
      markerPin.style.cursor = 'default';
      markerPin.style.pointerEvents = 'none';
      markerPin.style.flexShrink = '0';
      el.appendChild(markerPin);
      
      // Add rooftop name label to the right of the pin for destination
      if (rooftopName) {
        const labelEl = document.createElement('div');
        labelEl.className = 'route-marker-label-destination';
        labelEl.textContent = rooftopName;
        labelEl.style.marginLeft = '4px';
        labelEl.style.color = '#DC143C';
        labelEl.style.fontFamily = 'Roboto, sans-serif';
        labelEl.style.fontSize = '16px';
        labelEl.style.fontWeight = '600';
        labelEl.style.fontStyle = 'normal';
        labelEl.style.lineHeight = 'normal';
        labelEl.style.whiteSpace = 'nowrap';
        labelEl.style.WebkitTextStroke = '1px #FFF';
        labelEl.style.paintOrder = 'stroke fill';
        labelEl.style.pointerEvents = 'none';
        el.appendChild(labelEl);
      }
    } else {
      // Point A (start) - use different icons based on whether it's user's current location
      const isCurrentLocation = isUserLocation || rooftopName === 'Your Location';
      
      if (isCurrentLocation) {
        // Use blue dot for user's current location
        el.innerHTML = `
          <div class="current-location-pulse"></div>
          <div class="current-location-dot"></div>
        `;
      } else {
        // Use source icon for custom start location
        el.style.display = 'flex';
        el.style.flexDirection = 'row';
        el.style.alignItems = 'center';
        
        // Add rooftop name label to the left of the pin for source
        if (rooftopName && rooftopName !== 'Your Location') {
          const labelEl = document.createElement('div');
          labelEl.className = 'route-marker-label-source';
          labelEl.textContent = rooftopName;
          labelEl.style.marginRight = '4px';
          labelEl.style.color = '#2B2B2B';
          labelEl.style.fontFamily = 'Roboto, sans-serif';
          labelEl.style.fontSize = '16px';
          labelEl.style.fontWeight = '600';
          labelEl.style.fontStyle = 'normal';
          labelEl.style.lineHeight = 'normal';
          labelEl.style.whiteSpace = 'nowrap';
          labelEl.style.WebkitTextStroke = '1px #FFF';
          labelEl.style.paintOrder = 'stroke fill';
          labelEl.style.pointerEvents = 'none';
          el.appendChild(labelEl);
        }
        
        const markerPin = document.createElement('img');
        markerPin.src = '/icons/sourceIcon.png';
        markerPin.className = 'route-marker-pin-image';
        markerPin.style.width = '17.1px';
        markerPin.style.height = '17.1px';
        markerPin.style.cursor = 'default';
        markerPin.style.pointerEvents = 'none';
        markerPin.style.flexShrink = '0';
        el.appendChild(markerPin);
      }
    }

    // Create label for rooftop name if provided (skip for A, B, and stops since they're handled inline)
    if (rooftopName && label !== 'A' && label !== 'B' && !label.startsWith('stop-')) {
      const labelEl = document.createElement('div');
      labelEl.className = 'route-marker-label';
      labelEl.textContent = rooftopName;
      labelEl.style.marginTop = '4px';
      labelEl.style.padding = '2px 6px';
      labelEl.style.backgroundColor = 'transparent';
      labelEl.style.borderRadius = '4px';
      labelEl.style.fontSize = '11px';
      labelEl.style.fontWeight = '600';
      labelEl.style.color = '#333';
      labelEl.style.whiteSpace = 'nowrap';
      labelEl.style.textShadow = '0 1px 2px rgba(255,255,255,0.8), 0 0 4px rgba(255,255,255,0.5)';
      labelEl.style.textAlign = 'center';
      labelEl.style.maxWidth = '150px';
      labelEl.style.overflow = 'hidden';
      labelEl.style.textOverflow = 'ellipsis';
      labelEl.style.pointerEvents = 'none';
      el.appendChild(labelEl);
    }

    const enableDrag = !!routePointDragHandlerRef;
    if (enableDrag) {
      el.style.cursor = 'grab';
      el.style.pointerEvents = 'auto';
    }

    // Use 'right' anchor for source (A) with custom location so pin stays fixed while label extends left
    // Use 'center' anchor for source (A) with current location (rider icon)
    // Use 'top-left' anchor for destination (B) with offset so pin tip sits on the coordinate
    // Use 'left' anchor for stops so the icon sits at the coordinate with label extending right
    const isCurrentLocation = label === 'A' && (isUserLocation || rooftopName === 'Your Location');
    const markerAnchor = label === 'A' 
      ? (isCurrentLocation ? 'center' : 'right') 
      : (label === 'B' ? 'top-left' : 'left');

    // For destination (B), offset so the pin tip (bottom-center of 32px icon) aligns with the lngLat
    // For stops, offset so the icon center (8.5px into the 17.143px icon) aligns with the lngLat
    const markerOffset = label === 'B' ? [-16, -32] : (label.startsWith('stop-') ? [-8.5, 0] : [0, 0]);

    const marker = new maplibregl.Marker({
      element: el,
      anchor: markerAnchor,
      offset: markerOffset,
      draggable: enableDrag,
      pitchAlignment: 'viewport',
      rotationAlignment: 'map'
    })
      .setLngLat([lng, lat])
      .addTo(mapRef.current);

    // Ensure route markers render above POI icons
    const routeMarkerEl = marker.getElement();
    if (routeMarkerEl) routeMarkerEl.style.zIndex = '1500';

    // Store marker with label identifier
    marker._label = label;
    if (enableDrag) {
      marker.on('dragstart', () => {
        el.style.cursor = 'grabbing';
      });
      marker.on('dragend', () => {
        el.style.cursor = 'grab';
        const lngLat = marker.getLngLat();
        routePointDragHandlerRef.current?.(label, { lat: lngLat.lat, lng: lngLat.lng });
      });
    }
    routeMarkersRef.current.push(marker);

    return marker;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, routePointDragHandlerRef]);

  // Update route marker with rooftop name and bearing
  const updateRouteMarkerWithName = useCallback((label, rooftopName, lat, lng, bearing = null, options = {}) => {
    // Find and remove the old marker for this point
    const oldMarkerIndex = routeMarkersRef.current.findIndex(m => m._label === label);
    if (oldMarkerIndex !== -1) {
      routeMarkersRef.current[oldMarkerIndex].remove();
      routeMarkersRef.current.splice(oldMarkerIndex, 1);
    }

    // Add new marker with rooftop name and bearing
    addRouteMarker(lat, lng, label, rooftopName, bearing, options);
  }, [addRouteMarker]);

  // Draw route line on map - DEFINED FIRST to avoid "use before define"
  const drawRouteOnMap = useCallback((geometry, pointA, pointB, shouldFitBounds = true, duration = null, distance = null, finalDestination = null, currentTravelMode = 'auto', alternateRoutes = [], alternateIndexMap = null, stops = []) => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Check if map is loaded and valid
    if (!map || !map.getStyle || !map.getStyle()) return;

    // Use finalDestination if provided (when stops exist), otherwise use pointB
    // eslint-disable-next-line no-unused-vars
    const endPoint = finalDestination || pointB;

    // Remove existing route layer and source if present
    try {
      if (map.getLayer && map.getLayer('route-line')) {
        map.removeLayer('route-line');
      }
      if (map.getLayer && map.getLayer('route-line-outline')) {
        map.removeLayer('route-line-outline');
      }
      if (map.getLayer && map.getLayer('route-arrows')) {
        map.removeLayer('route-arrows');
      }
      if (map.getLayer && map.getLayer('route-arrows-center')) {
        map.removeLayer('route-arrows-center');
      }
      if (map.getLayer && map.getLayer('route-arrows-text')) {
        map.removeLayer('route-arrows-text');
      }
      if (map.getLayer && map.getLayer('route-connector-start')) {
        map.removeLayer('route-connector-start');
      }
      if (map.getLayer && map.getLayer('route-connector-end')) {
        map.removeLayer('route-connector-end');
      }
      if (map.getSource && map.getSource('route')) {
        map.removeSource('route');
      }
      if (map.getSource && map.getSource('route-connector-start')) {
        map.removeSource('route-connector-start');
      }
      if (map.getSource && map.getSource('route-connector-end')) {
        map.removeSource('route-connector-end');
      }
      // Remove stop connector layers and sources
      for (let i = 0; i < 20; i++) {
        if (map.getLayer && map.getLayer(`route-connector-stop-${i}`)) {
          map.removeLayer(`route-connector-stop-${i}`);
        }
        if (map.getSource && map.getSource(`route-connector-stop-${i}`)) {
          map.removeSource(`route-connector-stop-${i}`);
        }
      }
      // Remove alternate route layers and sources
      for (let i = 0; i < 5; i++) {
        if (map.getLayer && map.getLayer(`alternate-route-line-${i}`)) {
          map.removeLayer(`alternate-route-line-${i}`);
        }
        if (map.getLayer && map.getLayer(`alternate-route-outline-${i}`)) {
          map.removeLayer(`alternate-route-outline-${i}`);
        }
        if (map.getSource && map.getSource(`alternate-route-${i}`)) {
          map.removeSource(`alternate-route-${i}`);
        }
      }
      // Remove alternate route duration popups
      if (map._alternateRouteDurationPopups) {
        map._alternateRouteDurationPopups.forEach(popup => popup.remove());
        map._alternateRouteDurationPopups = [];
      }
      // Remove alternate route nudge proximity handler
      if (map._altRouteNudgeProximityHandler) {
        map.off('move', map._altRouteNudgeProximityHandler);
        map.off('zoom', map._altRouteNudgeProximityHandler);
        map._altRouteNudgeProximityHandler = null;
      }
      map._popupPixelPositions = [];
    } catch (error) {
      // Error removing route layer/source
    }

    if (routeArrowMarkersRef.current.length > 0) {
      routeArrowMarkersRef.current.forEach(marker => marker.remove());
      routeArrowMarkersRef.current = [];
    }

    if (routeArrowMarkersRef.current.length > 0) {
      routeArrowMarkersRef.current.forEach(marker => marker.remove());
      routeArrowMarkersRef.current = [];
    }
    if (routeArrowZoomHandlerRef.current && map.off) {
      map.off('zoomend', routeArrowZoomHandlerRef.current);
      routeArrowZoomHandlerRef.current = null;
    }

    // Add route source
    map.addSource('route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          duration: duration,
          distance: distance
        },
        geometry: geometry
      }
    });

    const style = map.getStyle();
    const labelLayer = style?.layers?.find(layer => (
      layer.type === 'symbol' &&
      layer.layout &&
      layer.layout['text-field'] &&
      (layer.id.includes('label') || layer.id.includes('highway_shields'))
    ));
    const firstRoadLabelId = labelLayer ? labelLayer.id : undefined;

    // Get vehicle icon based on travel mode (for alternate/unselected routes)
    const getUnselectedVehicleIcon = (mode) => {
      switch (mode) {
        case 'truck':
          return '<img src="/icons/truckUnselected.png" width="20" height="24" style="object-fit: contain;" />';
        case 'motorcycle':
          return '<img src="/icons/twoWheelerIcon.png" width="20" height="20" alt="motorcycle" style="object-fit:contain;opacity:0.6" />';
        case 'pedestrian':
          return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 5.5C14.6 5.5 15.5 4.6 15.5 3.5C15.5 2.4 14.6 1.5 13.5 1.5C12.4 1.5 11.5 2.4 11.5 3.5C11.5 4.6 12.4 5.5 13.5 5.5ZM9.8 8.9L7 23H9.1L10.9 15L13 17V23H15V15.5L12.9 13.5L13.5 10.5C14.8 12 16.8 13 19 13V11C17.1 11 15.5 10 14.7 8.6L13.7 7C13.3 6.4 12.7 6 12 6C11.7 6 11.5 6.1 11.2 6.1L6 8.3V13H8V9.6L9.8 8.9Z" fill="#888"/></svg>';
        case 'auto':
        default:
          return '<img src="/icons/carIcon.png" width="20" height="24" style="object-fit: contain;" />';
      }
    };

    // Get vehicle icon based on travel mode (for selected/main route)
    const getVehicleIcon = (mode) => {
      switch (mode) {
        case 'truck':
          return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 8H17V4H3C1.9 4 1 4.9 1 6V17H3C3 18.66 4.34 20 6 20C7.66 20 9 18.66 9 17H15C15 18.66 16.34 20 18 20C19.66 20 21 18.66 21 17H23V12L20 8ZM6 18.5C5.17 18.5 4.5 17.83 4.5 17C4.5 16.17 5.17 15.5 6 15.5C6.83 15.5 7.5 16.17 7.5 17C7.5 17.83 6.83 18.5 6 18.5ZM19.5 9.5L21.46 12H17V9.5H19.5ZM18 18.5C17.17 18.5 16.5 17.83 16.5 17C16.5 16.17 17.17 15.5 18 15.5C18.83 15.5 19.5 16.17 19.5 17C19.5 17.83 18.83 18.5 18 18.5Z" fill="#444"/></svg>';
        case 'motorcycle':
          return '<img src="/icons/twoWheelerIcon.png" width="20" height="20" alt="motorcycle" style="vertical-align:middle" />';
        case 'pedestrian':
          return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 5.5C14.6 5.5 15.5 4.6 15.5 3.5C15.5 2.4 14.6 1.5 13.5 1.5C12.4 1.5 11.5 2.4 11.5 3.5C11.5 4.6 12.4 5.5 13.5 5.5ZM9.8 8.9L7 23H9.1L10.9 15L13 17V23H15V15.5L12.9 13.5L13.5 10.5C14.8 12 16.8 13 19 13V11C17.1 11 15.5 10 14.7 8.6L13.7 7C13.3 6.4 12.7 6 12 6C11.7 6 11.5 6.1 11.2 6.1L6 8.3V13H8V9.6L9.8 8.9Z" fill="#444"/></svg>';
        case 'auto':
        default:
          return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5C5 13.67 5.67 13 6.5 13C7.33 13 8 13.67 8 14.5C8 15.33 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5C16 13.67 16.67 13 17.5 13C18.33 13 19 13.67 19 14.5C19 15.33 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z" fill="#444"/></svg>';
      }
    };

    // Draw alternate routes first (so they appear behind the main route)
    // Initialize popup position tracker for overlap prevention
    map._popupPixelPositions = [];
    // Pre-register route marker positions so popups avoid them
    if (routeMarkersRef && routeMarkersRef.current) {
      routeMarkersRef.current.forEach(rm => {
        try {
          const lngLat = rm.getLngLat();
          map._popupPixelPositions.push(map.project(lngLat));
        } catch (e) {
          // Ignore
        }
      });
    }
    // Also pre-register pointA, pointB, and finalDestination positions
    if (pointA) map._popupPixelPositions.push(map.project([pointA.lng, pointA.lat]));
    if (pointB) map._popupPixelPositions.push(map.project([pointB.lng, pointB.lat]));
    if (finalDestination) map._popupPixelPositions.push(map.project([finalDestination.lng, finalDestination.lat]));
    // Pre-register main route popup position so alternates avoid it
    if (duration && geometry.coordinates.length > 0) {
      const mainMidCoord = geometry.coordinates[Math.floor(geometry.coordinates.length / 2)];
      map._popupPixelPositions.push(map.project(mainMidCoord));
    }
    if (alternateRoutes && alternateRoutes.length > 0) {
      alternateRoutes.forEach((altRoute, index) => {
        const sourceId = `alternate-route-${index}`;
        const outlineLayerId = `alternate-route-outline-${index}`;
        const lineLayerId = `alternate-route-line-${index}`;

        // Add alternate route source
        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              alternateIndex: index
            },
            geometry: altRoute.geometry
          }
        });

        // Add outline layer (stroke) - unselected route styling
        map.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#5B80F7',
            'line-width': 12,
            'line-opacity': 0.8
          }
        }, firstRoadLabelId);

        // Add fill layer - unselected route styling
        map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#B6C7FB',
            'line-width': 9,
            'line-opacity': 0.9
          }
        }, firstRoadLabelId);

        // Add click handler for alternate route
        const clickHandler = (e) => {
          // Stop the event from propagating to the map's general click handler
          // This prevents RVG from being triggered when clicking alternate routes
          e.preventDefault();
          e.originalEvent?.stopPropagation();
          if (alternateRouteClickHandlerRef.current) {
            // Use the index map if provided, otherwise use visual index + 1
            const logicalIndex = alternateIndexMap ? alternateIndexMap[index] : (index + 1);
            alternateRouteClickHandlerRef.current(logicalIndex);
          }
        };

        // Add cursor change on hover
        map.on('mouseenter', lineLayerId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', lineLayerId, () => {
          map.getCanvas().style.cursor = '';
        });
        map.on('mouseenter', outlineLayerId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', outlineLayerId, () => {
          map.getCanvas().style.cursor = '';
        });

        // Add click handler to both layers
        map.on('click', lineLayerId, clickHandler);
        map.on('click', outlineLayerId, clickHandler);

        // Add duration popup for alternate route
        if (altRoute.duration && altRoute.distance) {
          const altHours = Math.floor(altRoute.duration / 3600);
          const altMinutes = Math.round((altRoute.duration % 3600) / 60);
          const altDurationText = altHours > 0 ? `${altHours}h ${altMinutes}min` : `${altMinutes} min`;
          const altDistanceKm = (altRoute.distance / 1000).toFixed(1);
          const altDistanceText = `${altDistanceKm} km`;

          // Find a non-overlapping coordinate along the alternate route
          const altCoordinates = altRoute.geometry.coordinates;
          // Try different positions along the route to avoid overlap with existing popups
          const candidateRatios = [0.5, 0.35, 0.65, 0.25, 0.75, 0.4, 0.6];
          let altRouteCoord = altCoordinates[Math.floor(altCoordinates.length / 2)];
          const popupHeight = 60; // approximate popup height in pixels

          for (const ratio of candidateRatios) {
            const candidateIdx = Math.floor(altCoordinates.length * ratio);
            const candidateCoord = altCoordinates[candidateIdx];
            const candidatePixel = map.project(candidateCoord);
            let hasOverlap = false;

            // Check against all previously placed popup positions
            if (map._popupPixelPositions) {
              for (const existingPixel of map._popupPixelPositions) {
                const dx = Math.abs(candidatePixel.x - existingPixel.x);
                const dy = Math.abs(candidatePixel.y - existingPixel.y);
                if (dx < 120 && dy < popupHeight) {
                  hasOverlap = true;
                  break;
                }
              }
            }

            if (!hasOverlap) {
              altRouteCoord = candidateCoord;
              if (!map._popupPixelPositions) map._popupPixelPositions = [];
              map._popupPixelPositions.push(candidatePixel);
              break;
            }
          }

          const altPopup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'route-duration-popup alternate-route-popup',
            anchor: 'left',
            offset: [8, 0]
          });

          altPopup
            .setLngLat(altRouteCoord)
            .setHTML(`
              <div class="route-duration-box alternate-route-duration-box">
                <div class="route-time-row">
                  <span class="route-vehicle-icon">${getUnselectedVehicleIcon(currentTravelMode)}</span>
                  <span class="route-time">${altDurationText}</span>
                </div>
                <div class="route-distance">${altDistanceText}</div>
              </div>
            `)
            .addTo(map);

          // Store popup reference for cleanup
          if (!map._alternateRouteDurationPopups) {
            map._alternateRouteDurationPopups = [];
          }
          map._alternateRouteDurationPopups.push(altPopup);
        }
      });
    }

    // Add proximity-based hiding for alternate route popups
    if (map._alternateRouteDurationPopups && map._alternateRouteDurationPopups.length > 0) {
      const checkAltNudgeProximity = () => {
        if (!map._alternateRouteDurationPopups) return;
        const proximityThreshold = 45;

        // Collect all route marker positions (source, stops, destination)
        const markerPositions = [];
        if (routeMarkersRef && routeMarkersRef.current) {
          routeMarkersRef.current.forEach(rm => {
            try {
              const lngLat = rm.getLngLat();
              markerPositions.push(map.project(lngLat));
            } catch (e) { /* ignore */ }
          });
        }

        map._alternateRouteDurationPopups.forEach(popup => {
          if (!popup || !popup._lngLat) return;
          const popupPixel = map.project(popup.getLngLat());
          let tooClose = false;

          for (const mp of markerPositions) {
            const dx = Math.abs(popupPixel.x - mp.x);
            const dy = Math.abs(popupPixel.y - mp.y);
            if (dx < proximityThreshold && dy < proximityThreshold) {
              tooClose = true;
              break;
            }
          }

          const popupEl = popup.getElement();
          if (popupEl) {
            popupEl.style.display = tooClose ? 'none' : '';
          }
        });
      };

      checkAltNudgeProximity();
      map.on('move', checkAltNudgeProximity);
      map.on('zoom', checkAltNudgeProximity);
      map._altRouteNudgeProximityHandler = checkAltNudgeProximity;
    }

    // Add route layer below labels so road names stay on top
    // First add the stroke/outline layer (wider, darker) - selected route styling
    map.addLayer({
      id: 'route-line-outline',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#3D55A5',
        'line-width': 12,
        'line-opacity': 1
      }
    }, firstRoadLabelId);

    // Then add the fill layer on top (narrower, lighter) - selected route styling
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#5B80F7',
        'line-width': 9,
        'line-opacity': 1
      }
    }, firstRoadLabelId);

    // Add click handler for main route (to select it when alternates are shown)
    const mainRouteClickHandler = (e) => {
      e.preventDefault();
      e.originalEvent?.stopPropagation();
      if (alternateRouteClickHandlerRef.current) {
        alternateRouteClickHandlerRef.current(0); // 0 is the main route
      }
    };

    // Only add click handlers if there are alternate routes to switch between
    if (alternateRoutes && alternateRoutes.length > 0) {
      // Add cursor change on hover for main route
      map.on('mouseenter', 'route-line', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'route-line', () => {
        map.getCanvas().style.cursor = '';
      });
      map.on('mouseenter', 'route-line-outline', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'route-line-outline', () => {
        map.getCanvas().style.cursor = '';
      });

      // Add click handler to main route layers
      map.on('click', 'route-line', mainRouteClickHandler);
      map.on('click', 'route-line-outline', mainRouteClickHandler);
    }

    const coordinates = geometry.coordinates;
    routeGeometryRef.current = geometry;

    // Draw connector lines from markers to route endpoints
    // Always use pointB for the end connector (not endPoint/finalDestination which is the last stop)
    if (coordinates && coordinates.length >= 2 && pointA && pointB) {
      const routeStart = coordinates[0];
      const routeEnd = coordinates[coordinates.length - 1];

      const markerStart = [pointA.lng, pointA.lat];
      const markerEnd = [pointB.lng, pointB.lat];

      // Start connector: curved line from source marker to route start
      map.addSource('route-connector-start', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: generateCurvedLine(markerStart, routeStart)
          }
        }
      });
      map.addLayer({
        id: 'route-connector-start',
        type: 'line',
        source: 'route-connector-start',
        layout: { 'line-join': 'round', 'line-cap': 'square' },
        paint: { 'line-color': '#7695F8', 'line-width': 4, 'line-dasharray': [1, 1], 'line-opacity': 1 }
      }, firstRoadLabelId);

      // End connector: curved line from route end to destination marker
      map.addSource('route-connector-end', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: generateCurvedLine(routeEnd, markerEnd)
          }
        }
      });
      map.addLayer({
        id: 'route-connector-end',
        type: 'line',
        source: 'route-connector-end',
        layout: { 'line-join': 'round', 'line-cap': 'square' },
        paint: { 'line-color': '#7695F8', 'line-width': 4, 'line-dasharray': [1, 1], 'line-opacity': 1 }
      }, firstRoadLabelId);

      // Stop connectors: curved dotted lines from each intermediate stop to the closest point on the route
      if (stops && stops.length > 0) {
        stops.forEach((stop, idx) => {
          if (!stop || !Number.isFinite(stop.lat) || !Number.isFinite(stop.lng)) return;

          const stopCoord = [stop.lng, stop.lat];

          // Find the closest point on the route geometry to this stop
          let closestPoint = coordinates[0];
          let minDist = Infinity;
          for (let i = 0; i < coordinates.length; i++) {
            const dx = coordinates[i][0] - stopCoord[0];
            const dy = coordinates[i][1] - stopCoord[1];
            const dist = dx * dx + dy * dy;
            if (dist < minDist) {
              minDist = dist;
              closestPoint = coordinates[i];
            }
          }

          const sourceId = `route-connector-stop-${idx}`;
          const layerId = `route-connector-stop-${idx}`;

          map.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: generateCurvedLine(closestPoint, stopCoord)
              }
            }
          });
          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'square' },
            paint: { 'line-color': '#7695F8', 'line-width': 4, 'line-dasharray': [1, 1], 'line-opacity': 1 }
          }, firstRoadLabelId);
        });
      }
    }

    // Create popup attached to route
    if (duration) {
      // Remove existing popup if present
      if (map._routeDurationPopup) {
        map._routeDurationPopup.remove();
        map._routeDurationPopup = null;
      }
      // Remove existing proximity handler
      if (map._routeNudgeProximityHandler) {
        map.off('move', map._routeNudgeProximityHandler);
        map.off('zoom', map._routeNudgeProximityHandler);
        map._routeNudgeProximityHandler = null;
      }

      const hours = Math.floor(duration / 3600);
      const minutes = Math.round((duration % 3600) / 60);
      const durationText = hours > 0 ? `${hours}h ${minutes}min` : `${minutes} min`;

      // Format distance (convert meters to km)
      const distanceKm = (distance / 1000).toFixed(1);
      const distanceText = `${distanceKm} km`;

      // Find a non-overlapping coordinate along the route (avoid route markers and their labels)
      const coordinates = geometry.coordinates;

      // Collect projected positions of route markers to avoid overlap
      const avoidPoints = [];
      if (routeMarkersRef && routeMarkersRef.current) {
        routeMarkersRef.current.forEach(rm => {
          try {
            const lngLat = rm.getLngLat();
            const projected = map.project(lngLat);
            // Estimate marker footprint (icon + label) as a generous radius
            avoidPoints.push({ x: projected.x, y: projected.y, radius: 100 });
          } catch (e) {
            // Ignore
          }
        });
      }

      // Also add pointA, pointB, finalDestination positions as fallback avoidance zones
      if (pointA) {
        const pp = map.project([pointA.lng, pointA.lat]);
        avoidPoints.push({ x: pp.x, y: pp.y, radius: 100 });
      }
      if (pointB) {
        const pp = map.project([pointB.lng, pointB.lat]);
        avoidPoints.push({ x: pp.x, y: pp.y, radius: 100 });
      }
      if (finalDestination) {
        const pp = map.project([finalDestination.lng, finalDestination.lat]);
        avoidPoints.push({ x: pp.x, y: pp.y, radius: 100 });
      }

      // Try multiple candidate positions along the route, pick the first that doesn't overlap
      const candidateRatios = [0.5, 0.4, 0.6, 0.35, 0.65, 0.3, 0.7, 0.25, 0.75, 0.2, 0.8];
      let routeCoord = coordinates[Math.floor(coordinates.length / 2)]; // fallback to midpoint

      for (const ratio of candidateRatios) {
        const candidateIdx = Math.floor(coordinates.length * ratio);
        const candidateCoord = coordinates[candidateIdx];
        const candidatePixel = map.project(candidateCoord);
        let hasOverlap = false;

        for (const ap of avoidPoints) {
          const dx = Math.abs(candidatePixel.x - ap.x);
          const dy = Math.abs(candidatePixel.y - ap.y);
          if (dx < ap.radius && dy < ap.radius) {
            hasOverlap = true;
            break;
          }
        }

        if (!hasOverlap) {
          routeCoord = candidateCoord;
          break;
        }
      }

      // Create popup anchored to left (arrow points left to route)
      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'route-duration-popup',
        anchor: 'left',
        offset: [8, 0]
      });

      popup
        .setLngLat(routeCoord)
        .setHTML(`
          <div class="route-duration-box">
            <div class="route-time-row">
              <span class="route-vehicle-icon">${getVehicleIcon(currentTravelMode)}</span>
              <span class="route-time">${durationText}</span>
            </div>
            <div class="route-distance">${distanceText}</div>
          </div>
        `)
        .addTo(map);

      // Store popup reference for cleanup
      map._routeDurationPopup = popup;

      // Hide nudge when it overlaps with source, stop, or destination markers
      const checkNudgeProximity = () => {
        if (!popup || !popup._lngLat) return;
        const popupPixel = map.project(popup.getLngLat());
        const proximityThreshold = 45; // pixels - tight threshold to only hide on actual visual overlap

        // Collect all route marker positions (source, stops, destination)
        const markerPositions = [];
        if (routeMarkersRef && routeMarkersRef.current) {
          routeMarkersRef.current.forEach(rm => {
            try {
              const lngLat = rm.getLngLat();
              markerPositions.push(map.project(lngLat));
            } catch (e) { /* ignore */ }
          });
        }

        let tooClose = false;
        for (const mp of markerPositions) {
          const dx = Math.abs(popupPixel.x - mp.x);
          const dy = Math.abs(popupPixel.y - mp.y);
          if (dx < proximityThreshold && dy < proximityThreshold) {
            tooClose = true;
            break;
          }
        }

        const popupEl = popup.getElement();
        if (popupEl) {
          popupEl.style.display = tooClose ? 'none' : '';
        }
      };

      // Check on initial render and on map move/zoom
      checkNudgeProximity();
      map.on('move', checkNudgeProximity);
      map.on('zoom', checkNudgeProximity);

      // Store handler reference for cleanup
      map._routeNudgeProximityHandler = checkNudgeProximity;
    }

    // Only fit bounds on initial draw
    if (shouldFitBounds) {
      const coordinates = geometry.coordinates;
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new maplibregl.LngLatBounds(coordinates[0], coordinates[0]));

      const isMweb = typeof document !== 'undefined' && document.body.classList.contains('mweb');
      const panelEl = isMweb ? document.querySelector('.route-details-panel') : null;
      const panelHeight = panelEl ? panelEl.getBoundingClientRect().height : 0;

      map.fitBounds(bounds, {
        padding: isMweb ? {
          top: 80,
          right: 40,
          bottom: Math.max(80, panelHeight + 20),
          left: 40
        } : {
          top: 80,
          right: 80,
          bottom: 80,
          left: 80 + 413
        },
        duration: 1000
      });
    }
  }, [mapRef]);

  // Fetch route from OSRM API - NOW DEFINED AFTER drawRouteOnMap
  const fetchRoute = useCallback(async (pointA, pointB, preFetchedPointADetails = null, preFetchedPointBDetails = null, stops = [], overrideTravelMode = null) => {
    try {
      if (!pointA || !pointB) return;

      const isSamePoint = Math.abs(pointA.lat - pointB.lat) < 1e-6 &&
        Math.abs(pointA.lng - pointB.lng) < 1e-6;

      if (isSamePoint) {
        setRouteDetails(null);
        setRouteDetailsLoading(false);
        hasZoomedToRouteRef.current = false;
        clearRouteVisualization();
        return;
      }

      // Increment fetch ID to track the latest request
      const currentFetchId = ++fetchRouteIdRef.current;

      setRouteDetailsLoading(true);
      setRouteDetails(null);

      // Clear old route visualization before fetching new one
      clearRouteVisualization();

      // Use override travel mode if provided, otherwise use state
      const effectiveTravelMode = overrideTravelMode || travelMode;
      const route = await getRoute(pointA, pointB, effectiveTravelMode, stops);

      // If a newer fetch was triggered while we were waiting, discard this result
      if (currentFetchId !== fetchRouteIdRef.current) return;

      // Calculate bearing from start point using first two coordinates
      let bearing = null;
      if (route.geometry && route.geometry.coordinates && route.geometry.coordinates.length >= 2) {
        const coords = route.geometry.coordinates;
        // Use first two coordinates for most accurate initial direction
        const startCoord = coords[0];
        const nextCoord = coords[1];
        bearing = calculateBearing(startCoord[1], startCoord[0], nextCoord[1], nextCoord[0]);
      }

      const getLabelFromDetails = (details) => {
        return details?.entity_name ||
          details?.rooftop_name ||
          details?.name ||
          details?.display_text ||
          details?.display_address ||
          details?.address ||
          '';
      };

      const isCoordinateLabel = (label) => {
        if (!label) return false;
        const normalized = String(label).trim();
        if (/^coordinates:/i.test(normalized)) return true;
        return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(normalized);
      };

      const shouldFetchPointA = !preFetchedPointADetails ||
        isCoordinateLabel(getLabelFromDetails(preFetchedPointADetails)) ||
        (!preFetchedPointADetails.entity_address && !preFetchedPointADetails.display_address && !preFetchedPointADetails.address);
      const shouldFetchPointB = !preFetchedPointBDetails &&
        !isCoordinateLabel(getLabelFromDetails(preFetchedPointBDetails));

      // Fetch location details for both points if missing or only coordinates
      const [pointADetails, pointBDetails] = await Promise.all([
        shouldFetchPointA ? fetchLocationDetails(pointA.lat, pointA.lng) : preFetchedPointADetails,
        shouldFetchPointB ? fetchLocationDetails(pointB.lat, pointB.lng) : preFetchedPointBDetails
      ]);

      // Check again after async work — a newer fetch may have started
      if (currentFetchId !== fetchRouteIdRef.current) return;

      const formatShortCoords = (lat, lng, precision = 4) => {
        const safeLat = Number(lat);
        const safeLng = Number(lng);
        if (!Number.isFinite(safeLat) || !Number.isFinite(safeLng)) {
          return '';
        }
        return `${safeLat.toFixed(precision)}, ${safeLng.toFixed(precision)}`;
      };

      // Create fallback data if API returns null
      const pointAShortCoords = formatShortCoords(pointA.lat, pointA.lng);
      const pointAData = pointADetails || {
        entity_name: pointA?.entity_name || pointA?.display_name || pointAShortCoords || 'Start Location',
        display_name: pointA?.entity_name || pointA?.display_name || pointAShortCoords || 'Start Location',
        entity_address: `${pointAShortCoords || `${pointA.lat.toFixed(6)}, ${pointA.lng.toFixed(6)}`}`,
        display_address: `${pointAShortCoords || `${pointA.lat.toFixed(6)}, ${pointA.lng.toFixed(6)}`}`,
        name: pointA?.entity_name || pointA?.display_name || pointAShortCoords || 'Start Location',
        rooftop_name: pointA?.entity_name || pointA?.display_name || pointAShortCoords || 'Start Location',
        building_name: pointA?.entity_name || pointA?.display_name || pointAShortCoords || 'Start Location',
        address: `${pointAShortCoords || `${pointA.lat.toFixed(6)}, ${pointA.lng.toFixed(6)}`}`,
        full_address: `${pointAShortCoords || `${pointA.lat.toFixed(6)}, ${pointA.lng.toFixed(6)}`}`,
        clean_address: `${pointAShortCoords || `${pointA.lat.toFixed(6)}, ${pointA.lng.toFixed(6)}`}`,
        location: { lat: pointA.lat, lon: pointA.lng }
      };

      const pointBShortCoords = formatShortCoords(pointB.lat, pointB.lng);
      const pointBData = pointBDetails || {
        entity_name: pointB?.entity_name || pointB?.display_name || pointBShortCoords || 'Destination',
        display_name: pointB?.entity_name || pointB?.display_name || pointBShortCoords || 'Destination',
        entity_address: `${pointBShortCoords || `${pointB.lat.toFixed(6)}, ${pointB.lng.toFixed(6)}`}`,
        display_address: `${pointBShortCoords || `${pointB.lat.toFixed(6)}, ${pointB.lng.toFixed(6)}`}`,
        name: pointB?.entity_name || pointB?.display_name || pointBShortCoords || 'Destination',
        rooftop_name: pointB?.entity_name || pointB?.display_name || pointBShortCoords || 'Destination',
        building_name: pointB?.entity_name || pointB?.display_name || pointBShortCoords || 'Destination',
        address: `${pointBShortCoords || `${pointB.lat.toFixed(6)}, ${pointB.lng.toFixed(6)}`}`,
        full_address: `${pointBShortCoords || `${pointB.lat.toFixed(6)}, ${pointB.lng.toFixed(6)}`}`,
        clean_address: `${pointBShortCoords || `${pointB.lat.toFixed(6)}, ${pointB.lng.toFixed(6)}`}`,
        location: { lat: pointB.lat, lon: pointB.lng }
      };

      // Update markers with display names and bearing for Point A
      updateRouteMarkerWithName('A', pointAData.entity_name || pointAData.rooftop_name || pointAData.name, pointA.lat, pointA.lng, bearing);
      
      // When no stops, B is the destination - update it now
      // When stops exist, we'll handle B marker after cleanup below
      if (stops.length === 0) {
        updateRouteMarkerWithName('B', pointBData.entity_name || pointBData.rooftop_name || pointBData.name, pointB.lat, pointB.lng);
      }

      // Get route summary (road names from maneuvers)
      let routeSummary = '';
      if (route.legs && route.legs.length > 0) {
        const leg = route.legs[0];
        
        // Extract street names from maneuvers (Delhivery format)
        if (leg.maneuvers && leg.maneuvers.length > 0) {
          const streetNames = [];
          leg.maneuvers.forEach(maneuver => {
            if (maneuver.street_names && maneuver.street_names.length > 0) {
              maneuver.street_names.forEach(name => {
                if (name && !streetNames.includes(name)) {
                  streetNames.push(name);
                }
              });
            }
          });
          
          // Join first 3 unique street names
          if (streetNames.length > 0) {
            routeSummary = streetNames.slice(0, 3).join(', ');
          }
        }
        // Fallback: Check for summary field if it's a string (OSRM format)
        else if (leg.summary && typeof leg.summary === 'string') {
          routeSummary = leg.summary;
        }
      }

      // Store route details with complete data (including legs for turn-by-turn directions)
      setRouteDetails({
        geometry: route.geometry,
        distance: route.distance,
        duration: route.duration,
        summary: routeSummary || 'Fastest Route',
        pointADetails: pointAData,
        pointBDetails: pointBData,
        legs: route.legs || [],
        alternateRoutes: route.alternateRoutes || []
      });

      // Always zoom to fit when stops are present, otherwise only on first draw
      const shouldZoom = stops.length > 0 || !hasZoomedToRouteRef.current;
      const finalDestination = stops.length > 0 ? stops[stops.length - 1] : null;
      drawRouteOnMap(route.geometry, pointA, pointB, shouldZoom, route.duration, route.distance, finalDestination, effectiveTravelMode, route.alternateRoutes || [], null, stops);
      if (!hasZoomedToRouteRef.current) {
        hasZoomedToRouteRef.current = true;
      }

      // Remove old stop markers (including B marker when stops exist) and add new ones
      routeMarkersRef.current
        .filter(m => m._label && (m._label.startsWith('stop-') || (stops.length > 0 && m._label === 'B')))
        .forEach(m => m.remove());
      routeMarkersRef.current = routeMarkersRef.current.filter(m => !m._label || (!m._label.startsWith('stop-') && !(stops.length > 0 && m._label === 'B')));

      if (stops.length > 0) {
        // Stops are the middle waypoints (between source and destination)
        // Add markers with rooftop name for each stop
        stops.forEach((stop, idx) => {
          addRouteMarker(stop.lat, stop.lng, `stop-${idx}`, stop.shortName || stop.name || `${idx + 1}`);
        });
        
        // Point B is the final destination - use destination marker
        addRouteMarker(pointB.lat, pointB.lng, 'B', pointBData?.entity_name || pointBData?.rooftop_name || pointBData?.name || null);
      }
    } catch (error) {
      // Error fetching route
    } finally {
      setRouteDetailsLoading(false);
    }
  }, [fetchLocationDetails, updateRouteMarkerWithName, addRouteMarker, drawRouteOnMap, calculateBearing, clearRouteVisualization, travelMode]);

  // Clear route and markers
  const clearRoute = useCallback(() => {
    setRoutePointA(null);
    setRoutePointB(null);
    setRouteDetails(null);

    // Reset zoom flag for next route
    hasZoomedToRouteRef.current = false;

    // Remove route markers
    routeMarkersRef.current.forEach(marker => marker.remove());
    routeMarkersRef.current = [];

    // Restore current location marker when clearing route
    if (currentLocationMarkerRef && currentLocationMarkerRef.current && mapRef.current) {
      // Re-add the current location marker if it was hidden
      const existingMarker = currentLocationMarkerRef.current;
      if (!existingMarker._map) {
        existingMarker.addTo(mapRef.current);
      }
    }

    // Remove route line from map
    clearRouteVisualization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, clearRouteVisualization]);

  // Toggle routing mode
  const toggleRoutingMode = useCallback(() => {
    if (isRoutingMode) {
      // Exiting routing mode - clear route
      clearRoute();
      setIsRoutingMode(false);
    } else {
      // Entering routing mode
      setIsRoutingMode(true);
    }
  }, [isRoutingMode, clearRoute]);

  return {
    isRoutingMode,
    setIsRoutingMode,
    routePointA,
    setRoutePointA,
    routePointB,
    setRoutePointB,
    routeDetails,
    setRouteDetails,
    routeDetailsLoading,
    setRouteDetailsLoading,
    travelMode,
    setTravelMode,
    routeMarkersRef,
    hasZoomedToRouteRef,
    addRouteMarker,
    updateRouteMarkerWithName,
    fetchRoute,
    drawRouteOnMap,
    clearRouteVisualization,
    clearRoute,
    toggleRoutingMode,
    alternateRouteClickHandlerRef,
  };
};

